import React from 'react';
import { Bolt, Sun, Moon, Activity } from 'lucide-react';

/**
 * Компонент раздела с параметрами потребления электроэнергии
 */
const ConsumptionParametersSection = ({ 
  formData, 
  handleInputChange, 
  errors
}) => {
  // Если выбран расчет по приборам, не показываем этот компонент
  if (formData.consumptionType === 'appliances') {
    return null;
  }

  return (
    <div className="mb-6 bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg border border-amber-200 dark:border-amber-800">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Bolt className="w-5 h-5 mr-2 text-yellow-500" />
        Потребление электроэнергии
      </h3>
      
      {/* Однотарифный учет */}
      {formData.tariffType === 'single' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="totalConsumption" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Общее потребление (кВт⋅ч)
            </label>
            <input
              type="text"
              id="totalConsumption"
              name="totalConsumption"
              value={formData.totalConsumption}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.totalConsumption ? 'input-error' : ''}`}
              placeholder="Введите потребление"
            />
            {errors.totalConsumption && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.totalConsumption}
              </p>
            )}
            <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
              Укажите потребление электроэнергии за месяц в киловатт-часах
            </p>
          </div>
        </div>
      )}

      {/* Двухзонный учет (день/ночь) */}
      {formData.tariffType === 'day_night' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="dayConsumption" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <Sun className="w-4 h-4 mr-2 text-yellow-500" />
              Дневное потребление (кВт⋅ч)
            </label>
            <input
              type="text"
              id="dayConsumption"
              name="dayConsumption"
              value={formData.dayConsumption}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.dayConsumption ? 'input-error' : ''}`}
              placeholder="Введите дневное потребление"
            />
            {errors.dayConsumption && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.dayConsumption}
              </p>
            )}
            <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
              С 7:00 до 23:00
            </p>
          </div>
          <div>
            <label htmlFor="nightConsumption" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <Moon className="w-4 h-4 mr-2 text-indigo-500" />
              Ночное потребление (кВт⋅ч)
            </label>
            <input
              type="text"
              id="nightConsumption"
              name="nightConsumption"
              value={formData.nightConsumption}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.nightConsumption ? 'input-error' : ''}`}
              placeholder="Введите ночное потребление"
            />
            {errors.nightConsumption && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.nightConsumption}
              </p>
            )}
            <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
              С 23:00 до 7:00
            </p>
          </div>
        </div>
      )}

      {/* Трехзонный учет */}
      {formData.tariffType === 'zones' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label htmlFor="peakConsumption" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <Activity className="w-4 h-4 mr-2 text-red-500" />
              Пиковая зона (кВт⋅ч)
            </label>
            <input
              type="text"
              id="peakConsumption"
              name="peakConsumption"
              value={formData.peakConsumption}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.peakConsumption ? 'input-error' : ''}`}
              placeholder="Введите пиковое потребление"
            />
            {errors.peakConsumption && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.peakConsumption}
              </p>
            )}
            <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
              Утро: 7:00-10:00, Вечер: 17:00-21:00
            </p>
          </div>
          <div>
            <label htmlFor="semipeakConsumption" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <Activity className="w-4 h-4 mr-2 text-yellow-500" />
              Полупиковая зона (кВт⋅ч)
            </label>
            <input
              type="text"
              id="semipeakConsumption"
              name="semipeakConsumption"
              value={formData.semipeakConsumption}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.semipeakConsumption ? 'input-error' : ''}`}
              placeholder="Введите полупиковое потребление"
            />
            {errors.semipeakConsumption && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.semipeakConsumption}
              </p>
            )}
            <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
              День: 10:00-17:00, Вечер: 21:00-23:00
            </p>
          </div>
          <div>
            <label htmlFor="offpeakConsumption" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <Moon className="w-4 h-4 mr-2 text-indigo-500" />
              Внепиковая зона (кВт⋅ч)
            </label>
            <input
              type="text"
              id="offpeakConsumption"
              name="offpeakConsumption"
              value={formData.offpeakConsumption}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.offpeakConsumption ? 'input-error' : ''}`}
              placeholder="Введите внепиковое потребление"
            />
            {errors.offpeakConsumption && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.offpeakConsumption}
              </p>
            )}
            <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
              Ночь: 23:00-7:00
            </p>
          </div>
        </div>
      )}

      {/* Информация о среднем потреблении */}
      <div className="mt-4 bg-white dark:bg-gray-700 p-3 rounded-lg border border-amber-200 dark:border-amber-800">
        <h4 className="text-sm font-medium text-amber-700 dark:text-amber-300 mb-2">Среднемесячное потребление в России:</h4>
        <ul className="text-xs text-amber-600 dark:text-amber-400 list-disc list-inside space-y-1">
          <li>Одинокий человек в квартире: 80-150 кВт⋅ч</li>
          <li>Семья из 2-3 человек: 150-300 кВт⋅ч</li>
          <li>Семья из 4-5 человек: 300-500 кВт⋅ч</li>
          <li>Частный дом без электрического отопления: 350-700 кВт⋅ч</li>
          <li>Дом с электрическим отоплением: 1500-3000 кВт⋅ч в зимний период</li>
        </ul>
      </div>
    </div>
  );
};

export default ConsumptionParametersSection;